import React, { useState, useRef, useEffect } from 'react';

/**
 * TiltTest — Module 6 (Bet Under Pressure)
 * Each round: player places a HIGH or LOW risk bet, then responds to a go/no-go stimulus.
 * Failure phase (rounds 1-10): deliberately hard — short display, lots of no-go traps.
 * Recovery phase (rounds 11-20): fair difficulty.
 *
 * Scoring captures:
 *   1. Recovery accuracy (did you perform better after the failure phase?)
 *   2. Rational betting (LOW bet after failure, HIGH after success = composed/not tilted)
 */
export default function TiltTest({ trials, onComplete, icon, moduleNum = 6, totalModules = 6 }) {
  const [uiState, setUiState] = useState('instructions'); // instructions | running
  const [phase, setPhase] = useState('betting');           // betting | fixation | stimulus
  const [prevOutcome, setPrevOutcome] = useState(null);    // null | 'success' | 'failure'
  const [stimulus, setStimulus] = useState(null);          // null | 'go' | 'nogo'
  const [feedbackMsg, setFeedbackMsg] = useState('');
  const [feedbackDelta, setFeedbackDelta] = useState(null); // +200 / -200 etc.
  const [progress, setProgress] = useState(0);
  const [currentPhaseLabel, setCurrentPhaseLabel] = useState('failure'); // for colour hint
  const [chips, setChips] = useState(1000);

  const HIGH_STAKE = 200;
  const LOW_STAKE  = 50;

  const phaseRef = useRef('betting');
  const s = useRef({
    trialIndex: 0,
    results: [],
    currentBet: null,
    prevOutcomeVal: null,
    onsetTime: null,
    awaitingResponse: false,
    timeoutId: null,
  });

  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const runStimulusRef = useRef(null);
  runStimulusRef.current = () => {
    const state = s.current;
    if (state.trialIndex >= trials.length) {
      onCompleteRef.current(state.results);
      return;
    }

    const trial = trials[state.trialIndex];
    setCurrentPhaseLabel(trial.phase);
    setProgress(Math.round((state.trialIndex / trials.length) * 100));
    setFeedbackMsg('');

    // ISI (fixation)
    phaseRef.current = 'fixation';
    setPhase('fixation');
    setStimulus(null);

    state.timeoutId = setTimeout(() => {
      // Show stimulus
      phaseRef.current = 'stimulus';
      setPhase('stimulus');
      setStimulus(trial.isGo ? 'go' : 'nogo');
      state.onsetTime = performance.now();
      state.awaitingResponse = true;

      // Auto-expire
      state.timeoutId = setTimeout(() => {
        if (!state.awaitingResponse) return;
        state.awaitingResponse = false;

        const correct = !trial.isGo; // no response on nogo = correct; miss on go = wrong
        recordResult(trial, correct, null);
      }, trial.displayDuration || 400);
    }, trial.isi || 600);
  };

  const recordResult = (trial, correct, rt) => {
    const state = s.current;
    const outcome = correct ? 'success' : 'failure';
    const stake = state.currentBet === 'high' ? HIGH_STAKE : LOW_STAKE;
    const delta = correct ? stake : -stake;

    setChips((prev) => Math.max(0, prev + delta));
    setFeedbackDelta(delta);

    state.results.push({
      type: 'tilt_bet',
      phase: trial.phase,
      bet: state.currentBet,
      prevOutcome: state.prevOutcomeVal,
      correct,
      rt,
      isGo: trial.isGo,
    });

    state.prevOutcomeVal = outcome;
    setPrevOutcome(outcome);

    if (!correct) {
      setFeedbackMsg(rt === null ? 'Too slow!' : 'Wrong!');
    }

    setStimulus(null);
    phaseRef.current = 'result';
    setPhase('result');

    state.trialIndex++;

    state.timeoutId = setTimeout(() => {
      if (state.trialIndex >= trials.length) {
        onCompleteRef.current(state.results);
        return;
      }
      // Reset bet for next round
      state.currentBet = null;
      setFeedbackDelta(null);
      phaseRef.current = 'betting';
      setPhase('betting');
      setFeedbackMsg('');
    }, 600);
  };

  // Keyboard: H / L for betting, SPACE for stimulus
  useEffect(() => {
    if (uiState !== 'running') return;

    const handleKey = (e) => {
      const state = s.current;
      const p = phaseRef.current;

      if (p === 'betting') {
        if (e.code === 'KeyH') { e.preventDefault(); commitBet('high'); }
        if (e.code === 'KeyL') { e.preventDefault(); commitBet('low'); }
        return;
      }

      if (p === 'stimulus' && e.code === 'Space') {
        e.preventDefault();
        if (!state.awaitingResponse || state.onsetTime === null) return;
        const trial = trials[state.trialIndex];
        if (!trial) return;
        clearTimeout(state.timeoutId);
        const rt = Math.round(performance.now() - state.onsetTime);
        state.awaitingResponse = false;
        const correct = trial.isGo;
        recordResult(trial, correct, rt);
      }
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [uiState, trials]); // eslint-disable-line

  const commitBet = (bet) => {
    if (phaseRef.current !== 'betting') return;
    s.current.currentBet = bet;
    runStimulusRef.current();
  };

  const startTest = () => {
    const state = s.current;
    state.trialIndex = 0;
    state.results = [];
    state.currentBet = null;
    state.prevOutcomeVal = null;
    state.awaitingResponse = false;
    setPrevOutcome(null);
    setFeedbackMsg('');
    setFeedbackDelta(null);
    setChips(1000);
    setProgress(0);
    phaseRef.current = 'betting';
    setPhase('betting');
    setUiState('running');
  };

  useEffect(() => {
    return () => {
      clearTimeout(s.current.timeoutId);
      s.current.awaitingResponse = false;
    };
  }, []);

  // --- Render ---

  if (uiState === 'instructions') {
    return (
      <div className="card text-center" style={{ maxWidth: 520, margin: '0 auto' }}>
        {icon && (
          <div style={{ width: 56, height: 56, borderRadius: 14, background: 'var(--accent-dim)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)', margin: '0 auto 20px' }}>
            {icon}
          </div>
        )}
        <h2 style={{ marginBottom: 8 }}>Tilt Recovery</h2>
        <p style={{ marginBottom: 14, lineHeight: 1.7 }}>
          Before each round, choose your risk level: <strong style={{ color: '#ef4444' }}>HIGH (±200 chips)</strong> or{' '}
          <strong style={{ color: '#00d4ff' }}>LOW (±50 chips)</strong>.
          Then respond to the stimulus — <span className="text-accent mono">SPACE</span> on green circles, nothing on red X marks.
        </p>
        <p style={{ marginBottom: 14, lineHeight: 1.6, fontSize: 14, color: 'var(--text-secondary)' }}>
          You start with 1000 chips. The first half is deliberately unfair — stay composed.
          Your betting decisions and recovery accuracy are what we measure.
        </p>
        <p className="text-muted" style={{ fontSize: 13, marginBottom: 24 }}>
          Keys: <span className="text-accent mono">H</span> = High risk &nbsp;·&nbsp;
          <span className="text-accent mono">L</span> = Low risk &nbsp;·&nbsp;
          <span className="text-accent mono">SPACE</span> = Go response
        </p>
        <button className="btn btn--primary" onClick={startTest}>Start</button>
      </div>
    );
  }

  const isFailurePhase = currentPhaseLabel === 'failure';

  return (
    <div style={{ width: '100%' }}>
      {/* Progress bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div className="progress-bar" style={{ flex: 1 }}>
          <div className="progress-bar__fill" style={{ width: `${progress}%` }} />
        </div>
        <span className="text-muted" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>{progress}%</span>
      </div>

      <div style={{ maxWidth: 520, margin: '0 auto' }}>

        {/* Betting phase */}
        {phase === 'betting' && (
          <div className="card" style={{ textAlign: 'center', padding: '36px 24px' }}>
            {/* Chip counter */}
            <div style={{ marginBottom: 20 }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                fontSize: 22, fontWeight: 900, color: chips >= 1000 ? '#10b981' : chips >= 600 ? '#f59e0b' : '#ef4444',
              }}>
                {chips}
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: 1 }}>CHIPS</span>
              </span>
            </div>

            {/* Previous outcome */}
            {prevOutcome && feedbackDelta !== null && (
              <div style={{ marginBottom: 20 }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  fontSize: 13, fontWeight: 700, padding: '5px 14px', borderRadius: 20,
                  background: feedbackDelta > 0 ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)',
                  color: feedbackDelta > 0 ? '#10b981' : '#ef4444',
                  border: `1px solid ${feedbackDelta > 0 ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
                }}>
                  {feedbackDelta > 0 ? `+${feedbackDelta}` : feedbackDelta}
                </span>
              </div>
            )}

            <div style={{ fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 8 }}>
              Place your bet
            </div>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 28, lineHeight: 1.5 }}>
              How confident are you going into this round?
            </p>

            <div style={{ display: 'flex', gap: 14, justifyContent: 'center' }}>
              <button
                onClick={() => commitBet('high')}
                style={{
                  flex: 1, maxWidth: 180, padding: '18px 12px', borderRadius: 10, cursor: 'pointer',
                  border: '2px solid rgba(239,68,68,0.4)', background: 'rgba(239,68,68,0.07)',
                  color: '#ef4444', fontWeight: 700, fontSize: 15, lineHeight: 1.3,
                  transition: 'background 0.15s, border-color 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.16)'; e.currentTarget.style.borderColor = '#ef4444'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.07)'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.4)'; }}
              >
                <div style={{ fontSize: 11, letterSpacing: 1, opacity: 0.7, marginBottom: 4 }}>H KEY</div>
                HIGH RISK
                <div style={{ fontSize: 12, fontWeight: 700, color: '#ef4444', marginTop: 6 }}>±{HIGH_STAKE} chips</div>
              </button>

              <button
                onClick={() => commitBet('low')}
                style={{
                  flex: 1, maxWidth: 180, padding: '18px 12px', borderRadius: 10, cursor: 'pointer',
                  border: '2px solid rgba(0,212,255,0.35)', background: 'rgba(0,212,255,0.06)',
                  color: '#00d4ff', fontWeight: 700, fontSize: 15, lineHeight: 1.3,
                  transition: 'background 0.15s, border-color 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(0,212,255,0.14)'; e.currentTarget.style.borderColor = '#00d4ff'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(0,212,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(0,212,255,0.35)'; }}
              >
                <div style={{ fontSize: 11, letterSpacing: 1, opacity: 0.7, marginBottom: 4 }}>L KEY</div>
                LOW RISK
                <div style={{ fontSize: 12, fontWeight: 700, color: '#00d4ff', marginTop: 6 }}>±{LOW_STAKE} chips</div>
              </button>
            </div>
          </div>
        )}

        {/* Fixation / ISI */}
        {phase === 'fixation' && (
          <div style={{
            height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: isFailurePhase ? 'rgba(30,12,12,0.6)' : 'rgba(10,14,26,0.6)',
            borderRadius: 12, border: '1px solid var(--border)',
          }}>
            <span style={{ fontSize: 36, color: isFailurePhase ? '#3a2a1a' : '#4b5a72', fontWeight: 700 }}>+</span>
          </div>
        )}

        {/* Stimulus */}
        {phase === 'stimulus' && (
          <div style={{
            height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: isFailurePhase ? 'rgba(30,12,12,0.6)' : 'rgba(10,14,26,0.6)',
            borderRadius: 12, border: '1px solid var(--border)',
            position: 'relative',
          }}>
            {stimulus === 'go' && (
              <div style={{
                width: 90, height: 90, borderRadius: '50%',
                background: 'rgba(16,185,129,0.2)', border: '3px solid #10b981',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'rgba(16,185,129,0.7)', letterSpacing: 1 }}>SPACE</span>
              </div>
            )}
            {stimulus === 'nogo' && (
              <svg width="80" height="80" viewBox="0 0 80 80">
                <line x1="18" y1="18" x2="62" y2="62" stroke="#ef4444" strokeWidth="10" strokeLinecap="round" />
                <line x1="62" y1="18" x2="18" y2="62" stroke="#ef4444" strokeWidth="10" strokeLinecap="round" />
              </svg>
            )}
            {feedbackMsg && (
              <div style={{
                position: 'absolute', top: 24, left: '50%', transform: 'translateX(-50%)',
                color: '#ef4444', fontSize: 16, fontWeight: 700,
                animation: 'fadeOut 0.4s ease forwards',
                whiteSpace: 'nowrap',
              }}>
                {feedbackMsg}
              </div>
            )}
          </div>
        )}

        {/* Brief result flash */}
        {phase === 'result' && (
          <div style={{
            height: 280, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
            background: isFailurePhase ? 'rgba(30,12,12,0.6)' : 'rgba(10,14,26,0.6)',
            borderRadius: 12, border: '1px solid var(--border)',
          }}>
            {feedbackDelta !== null && (
              <span style={{
                fontSize: 32, fontWeight: 900,
                color: feedbackDelta > 0 ? '#10b981' : '#ef4444',
              }}>
                {feedbackDelta > 0 ? `+${feedbackDelta}` : feedbackDelta}
              </span>
            )}
            {feedbackMsg && (
              <span style={{ color: '#ef4444', fontSize: 15, fontWeight: 700 }}>{feedbackMsg}</span>
            )}
          </div>
        )}
      </div>

      <p className="text-center text-muted" style={{ fontSize: 13, marginTop: 14 }}>
        Module {moduleNum} of {totalModules} — Tilt Recovery
      </p>
    </div>
  );
}
